// Centralized utility functions for the appointment management system
// Handles risk status, appointment status, data transformations, UI helpers, and date formatting

import IMAGES from '../constant/images';

// Constants for color consistency
const COLORS = {
  RISK: {
    HIGH: '#DC2626',
    MODERATE: '#FF7439',
    LOW: '#FFEE33',
    NO_RISK: '#B7E049',
    NA: '#B2BEB5'
  },
  STATUS: {
    UPCOMING: '#FFE375',
    COMPLETED: '#98FF8A',
    PENDING: '#FFAF77',
    CANCELLED: '#FF7439',
    EXPIRED: '#DC2626',
    MISSED: '#DC2626',
    DEFAULT: '#4B61FF'
  },
  TEXT: {
    WHITE: '#FFFFFF',
    DARK: '#374151'
  }
};

// Get risk status background color
const getRiskColor = (risk) => {
  if (risk === "HIGH_RISK" || risk === "HIGH") {
    return "bg-[#DC2626]";
  } else if (risk === "MODERATE_RISK" || risk === "MODERATE") {
    return "bg-[#FF7439]";
  } else if (risk === "LOW_RISK" || risk === "LOW") {
    return "bg-[#FFEE33]";
  } else if (risk === "NO_RISK" || risk === "NO RISK") {
    return "bg-[#B7E049]"; // Green for NO RISK
  } else {
    return "bg-[#B2BEB5]"; // Gray for N/A or null
  }
};

// Get risk status text color
const getRiskTextColor = (risk) => {
  if (risk === "N/A" || risk === null || risk === undefined || risk === "NO_RISK" || risk === "NO RISK" || risk === "LOW_RISK" || risk === "LOW") {
    return "text-[#374151]"; // Dark grey text for better contrast on light backgrounds
  } else {
    return "text-[#FFFFFF]";
  }
};

// Get status dot color
const getStatusColor = (status, currentStatus = null) => {
  const effectiveStatus = getEffectiveStatus(status, currentStatus);

  if (effectiveStatus === "SCHEDULED" || effectiveStatus === "Upcoming" || effectiveStatus === "UPCOMING") {
    return "bg-[#FFE375]"; // Yellow for upcoming
  } else if (effectiveStatus === "COMPLETED" || effectiveStatus === "COMPLETE" || effectiveStatus === "Completed") {
    return "bg-[#98FF8A]";
  } else if (effectiveStatus === "PENDING") {
    return "bg-[#FFAF77]";
  } else if (effectiveStatus === "CANCELED" || effectiveStatus === "CANCELLED") {
    return "bg-[#FF7439]";
  } else if (effectiveStatus === "EXPIRED") {
    return "bg-[#DC2626]";
  } else if (effectiveStatus === "IN_COMPLETE" || effectiveStatus === "INCOMPLETE") {
    return "bg-[#DC2626]";
  } else if (effectiveStatus === "IN_PROGRESS") {
    return "bg-[#DC2626]";
  } else if (effectiveStatus === "COUNSELOR_UNAVAILABLE" || effectiveStatus === "COUNSELLOR UNAVAILABLE") {
    return "bg-[#FF7439]";
  } else if (effectiveStatus === "MISSED" || effectiveStatus === "Missed" || effectiveStatus === "COUNSELLOR MISSED" || effectiveStatus === "STUDENT MISSED" || effectiveStatus === "MISSED BY BOTH" || effectiveStatus === "MISSED_BY_BOTH") {
    return "bg-[#DC2626]"; // Red color for missed appointments
  } else if (effectiveStatus === "RISK MISSING") {
    return "bg-[#DC2626]"; // Red for risk missing
  } else {
    return "bg-[#4B61FF]"; // Default blue
  }
};

// Get the effective status to display (handles currentStatus for SCHEDULED appointments)
const getEffectiveStatus = (status, currentStatus) => {
  if (status === "SCHEDULED" && currentStatus) {
    return currentStatus;
  }
  return status;
};

// Get status text display
const getStatusText = (status, currentStatus = null) => {
  const effectiveStatus = getEffectiveStatus(status, currentStatus);

  if (effectiveStatus === "SCHEDULED" || effectiveStatus === "Upcoming" || effectiveStatus === "UPCOMING") {
    return "Upcoming";
  } else if (effectiveStatus === "COMPLETE" || effectiveStatus === "COMPLETED") {
    return "Completed";
  } else if (effectiveStatus === "PENDING") {
    return "Pending";
  } else if (effectiveStatus === "CANCELED") {
    return "Cancelled";
  } else if (effectiveStatus === "EXPIRED") {
    return "Expired";
  } else if (effectiveStatus === "IN_COMPLETE" || effectiveStatus === "INCOMPLETE") {
    return "In Complete";
  } else if (effectiveStatus === "IN_PROGRESS") {
    return "In Progress";
  } else if (effectiveStatus === "STUDENT MISSED" || effectiveStatus === "STUDENT_MISSED") {
    return "Student Missed";
  } else if (effectiveStatus === "COUNSELLOR MISSED" || effectiveStatus === "COUNSELLOR_MISSED") {
    return "Counselor Missed";
  } else if (effectiveStatus === "MISSED BY BOTH" || effectiveStatus === "MISSED_BY_BOTH") {
    return "Missed By Both";
  } else if (effectiveStatus === "COUNSELOR_UNAVAILABLE" || effectiveStatus === "COUNSELLOR UNAVAILABLE") {
    return "Counselor Unavailable";
  } else if (effectiveStatus === "MISSED" || effectiveStatus === "Missed" || effectiveStatus === "MISSED BY COUNSELLOR") {
    return "Missed";
  } else if (effectiveStatus === "RISK MISSING") {
    return "Risk Missing";
  } else {
    return effectiveStatus; // Return as-is if no mapping found
  }
};

// Get risk text display (separate function for risk status)
const getRiskText = (riskStatus) => {
  if (riskStatus === "HIGH_RISK") {
    return "HIGH";
  } else if (riskStatus === "LOW_RISK") {
    return "LOW";
  } else if (riskStatus === "NO_RISK") {
    return "NO RISK";
  } else if (riskStatus === "MODERATE_RISK") {
    return "MODERATE";
  } else if (riskStatus === "N/A" || riskStatus === null || riskStatus === undefined) {
    return "N/A";
  } else {
    return riskStatus; // Return as-is if no mapping found
  }
};

// Get risk badge with proper styling (updated to use getRiskText)
const getRiskStatusBadge = (riskStatus) => {
  const backgroundColor = getRiskColor(riskStatus);
  const textColor = getRiskTextColor(riskStatus);
  const displayText = getRiskText(riskStatus);
  
  return {
    backgroundColor: backgroundColor.replace('bg-[', '').replace(']', ''),
    color: textColor.replace('text-[', '').replace(']', ''),
    text: displayText
  };
};


// Transform appointment data from API format to display format
const transformAppointmentData = (apiItem) => {
  if (!apiItem) return null;
  
  return {
    ...apiItem,
    // Transform status for display
    displayStatus: getStatusText(apiItem.status),
    statusColor: getStatusColor(apiItem.status),
    
    // Transform risk for display
    displayRisk: getRiskText(apiItem.riskStatus),
    riskColor: getRiskColor(apiItem.riskStatus),
    riskTextColor: getRiskTextColor(apiItem.riskStatus),
    
    // Keep original values for filtering and backend operations
    originalStatus: apiItem.status,
    originalRisk: apiItem.riskStatus
  };
};

// Transform appointments list from API response
const transformAppointmentsList = (apiData) => {
  if (!apiData || !Array.isArray(apiData)) return [];
  
  return apiData.map(transformAppointmentData);
};

// Transform filter values from display format to API format
const transformFiltersForAPI = (filters) => {
  if (!filters) return {};

  const apiFilters = {};

  // Transform status from display to API format
  if (filters.status && filters.status.length > 0) {
    const statusMap = {
      'Upcoming': 'SCHEDULED',
      'Completed': 'COMPLETED',
      'Pending': 'PENDING',
      'Cancelled': 'CANCELED',
      'Member Missed': 'STUDENT_MISSED',
      'Counsellor Missed': 'COUNSELLOR_MISSED',
      'Both Missed': 'MISSED_BY_BOTH',
      'Incomplete': 'IN_COMPLETE',
      'Unavailable': 'COUNSELOR_UNAVAILABLE'
    };
    apiFilters.status = filters.status.map(status => statusMap[status] || status).join(',');
  }

  // Transform mode from display to API format
  if (filters.mode && filters.mode.length > 0) {
    const modeMap = {
      'Video call': 'VIDEO',
      'Audio call': 'AUDIO',
      'In-person': 'INPERSON',
      'Chat': 'CHAT'
    };
    apiFilters.mode = filters.mode.map(mode => modeMap[mode] || mode).join(',');
  }

  // Transform risk status from display to API format
  if (filters.riskStatus && filters.riskStatus.length > 0) {
    const riskMap = {
      'No Risk': 'NO_RISK',
      'High': 'HIGH_RISK',
      'Moderate': 'MODERATE_RISK',
      'Low': 'LOW_RISK',
      'NA': null
    };
    apiFilters.riskStatus = filters.riskStatus.map(risk => riskMap[risk] || risk).join(',');
  }

  // Transform appointment type
  if (filters.type && filters.type.length > 0) {
    const typeMap = {
      'New': 'New',
      'Follow-up': 'Followup'
    };
    apiFilters.appointmentType = filters.type.map(type => typeMap[type] || type).join(',');
  }

  // Pass through practitioner and member names as-is
  if (filters.practitionerNames && filters.practitionerNames.length > 0) {
    apiFilters.practitionerName = filters.practitionerNames.join(',');
  }

  if (filters.memberNames && filters.memberNames.length > 0) {
    apiFilters.memberName = filters.memberNames.join(',');
  }

  // Pass through date filters
  if (filters.startDate) {
    apiFilters.startDate = filters.startDate;
  }

  if (filters.endDate) {
    apiFilters.endDate = filters.endDate;
  }

  return apiFilters;
};

// Transform practitioner filters from UI to API format
const transformPractitionerFiltersForAPI = (filters) => {
  if (!filters) return {};

  const apiFilters = {};

  // Transform status from display to API format
  if (filters.status && filters.status.length > 0) {
    const statusMap = {
      'Active': 'ACTIVE',
      'Inactive': 'INACTIVE'
    };

    // Convert all selected statuses to API format
    const apiStatuses = filters.status
      .map(status => statusMap[status])
      .filter(Boolean); // Remove undefined values

    if (apiStatuses.length > 0) {
      // For API, send as comma-separated string or first value
      apiFilters.status = apiStatuses.length === 1 ? apiStatuses[0] : apiStatuses.join(',');
    }
  }

  // Transform availability from display to API format
  if (filters.availability && filters.availability.length > 0) {
    const availabilityMap = {
      'Available Today': true,
      'Not Available': false
    };

    // Handle availability logic
    const hasAvailableToday = filters.availability.includes('Available Today');
    const hasNotAvailable = filters.availability.includes('Not Available');

    if (hasAvailableToday && !hasNotAvailable) {
      apiFilters.availableToday = true;
    } else if (hasNotAvailable && !hasAvailableToday) {
      apiFilters.availableToday = false;
    }
    // If both are selected, don't add the filter (show all)
  }

  return apiFilters;
};

// Utility function to get mode icon
const getModeIcon = (mode) => {

  if (mode === "Audio Call" || mode === "AUDIO") {
    return IMAGES.audio_call;
  } else if (mode === "Video Call" || mode === "VIDEO") {
    return IMAGES.video_icon;
  } else if (mode === "In Person" || mode === "INPERSON") {
    return IMAGES.in_person;
  } else {
    return IMAGES.chat;
  }
};

// Utility function to format time consistently
const formatTime = (timeString) => {
  if (!timeString) return "";
  const [hours, minutes] = timeString.split(":");
  const date = new Date();
  date.setHours(hours, minutes);
  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Utility function to calculate duration
const calculateDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return "N/A";

  try {
    const [startHours, startMinutes] = startTime.split(":").map(Number);
    const [endHours, endMinutes] = endTime.split(":").map(Number);

    const startDate = new Date();
    startDate.setHours(startHours, startMinutes, 0, 0);

    const endDate = new Date();
    endDate.setHours(endHours, endMinutes, 0, 0);

    // Handle overnight appointments (end time is next day)
    if (endDate <= startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }

    const diffMs = endDate - startDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 60) {
      return `${diffMinutes} minutes`;
    } else {
      const hours = Math.floor(diffMinutes / 60);
      const remainingMinutes = diffMinutes % 60;
      if (remainingMinutes === 0) {
        return `${hours} hour${hours > 1 ? 's' : ''}`;
      } else {
        return `${hours} hour${hours > 1 ? 's' : ''} ${remainingMinutes} minutes`;
      }
    }
  } catch (error) {
    // Silent error handling for production
    return "N/A";
  }
};

// Utility function to format symptom level
const formatSymptomLevel = (level) => {
  if (!level || level === "None") return "None";
  return level.charAt(0).toUpperCase() + level.toLowerCase().slice(1);
};

// Utility function to format API date from YYYY-MM-DD to DD-MM-YYYY
const formatApiDate = (dateStr) => {
  if (!dateStr) return "";
  // Convert from yyyy-mm-dd to dd-mm-yyyy format
  const dateParts = dateStr.split('-');
  if (dateParts.length === 3) {
    return `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
  }
  return dateStr;
};

// Utility function to calculate age from date of birth
const calculateAge = (dateOfBirth, formatDate = formatApiDate) => {
  if (!dateOfBirth) return "";
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return `${age}yr / ${formatDate(dateOfBirth)}`;
};

// Utility function to get the top mode from trend data
const getTopMode = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    return "No_Mode";
  }

  const modeLabels = {
    video: "Video",
    chat: "Chat",
    audio: "Audio",
    inPerson: "In-person",
  };

  const totals = data.reduce(
    (acc, curr) => {
      acc.video += curr.video || 0;
      acc.chat += curr.chat || 0;
      acc.audio += curr.audio || 0;
      acc.inPerson += curr.inPerson || 0;
      return acc;
    },
    { video: 0, chat: 0, audio: 0, inPerson: 0 }
  );

  const totalSum = Object.values(totals).reduce((sum, val) => sum + val, 0);

  if (totalSum === 0) {
    return "No_Mode";
  }

  const topCategoryKey = Object.keys(totals).reduce((a, b) =>
    totals[a] > totals[b] ? a : b
  );

  return modeLabels[topCategoryKey];
};

export {
  getRiskColor,
  getRiskTextColor,
  getStatusColor,
  getStatusText,
  getRiskText,
  getRiskStatusBadge,
  getEffectiveStatus,
  transformAppointmentData,
  transformAppointmentsList,
  transformFiltersForAPI,
  transformPractitionerFiltersForAPI,
  getModeIcon,
  formatTime,
  calculateDuration,
  formatSymptomLevel,
  formatApiDate,
  calculateAge,
  getTopMode
};